import React, { useCallback, KeyboardEvent } from 'react';
import { generateImageSrc, getDetailedShopBackgroundImageUrl, isShopOnlyTakeAway } from '@box/utils';
import { ImageTile } from '@box/component-library';
import { translate } from '@box-core/services';
import { PromoTagComponent } from '../promo-tag';
import { ShopRating } from '../shop-rating';
import { ShopTileDeliveryInfo } from '../shop-tile-delivery-info';
import { ShopTileProps } from '../shop-tile/shop-tile.types';

const ShopTileDetailed: React.FC<ShopTileProps> = ({ shop, disableAnimation, onRouterClick }) => {
  const { name, logo, isNew, locationKey, vanity_url, disabledText, promo, promotedTag } = shop;

  const logoUrl = generateImageSrc(logo);
  const backgroundImageUrl = getDetailedShopBackgroundImageUrl(shop);
  const hasTakeAwayLabel = isShopOnlyTakeAway(shop);

  const routerLink = `/delivery/${locationKey}/${vanity_url}`;
  const firstGroupTagOptions = promo?.firstGroupLongOptions;
  const secondGroupTagOptions = promo?.secondGroupLongOptions;
  const showTags = Boolean(firstGroupTagOptions || secondGroupTagOptions);
  const showPromotedTag = Boolean(promotedTag);
  const translateReference = translate;

  const onShopClick = useCallback(() => {
    onRouterClick(routerLink);
  }, [onRouterClick]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        onRouterClick(routerLink);
      }
    },
    [onRouterClick]
  );

  return (
    <div className="shop-tile-detailed-component">
      <div
        onClick={() => onShopClick()}
        onKeyDown={(e) => handleKeyDown(e)}
        role="link"
        tabIndex={0}
        className={`shop-tile-detailed-component-link${
          disabledText ? ' shop-tile-detailed-component-link-disabled' : ''
        }`}
      >
        <div className="shop-tile-detailed-component-header">
          {showTags && (
            <div className="shop-tile-detailed-component-header-tags">
              {firstGroupTagOptions && (
                <PromoTagComponent options={firstGroupTagOptions} enableAnimations={!disableAnimation} />
              )}
              {secondGroupTagOptions && (
                <PromoTagComponent options={secondGroupTagOptions} enableAnimations={!disableAnimation} />
              )}
            </div>
          )}

          <div className="shop-tile-detailed-component-header-logo image-tile">
            <ImageTile imgUrl={logoUrl} altText={`${name} logo`} disabled={!!disabledText} />
          </div>

          <div
            className={`shop-tile-detailed-component-header-background-image hoverable-animated image-tile${
              backgroundImageUrl.generated ? ' image-tile-generated' : ''
            }`}
          >
            <ImageTile
              imgUrl={backgroundImageUrl.url}
              bottomRightOverlayText={hasTakeAwayLabel ? 'Take Away' : null}
              translate={translateReference}
              altText={name}
              overlayText={disabledText}
              generated={backgroundImageUrl.generated}
            />
          </div>

          {showPromotedTag && (
            <span className="shop-tile-detailed-component-header-promoted-tag ods-typography-microcopy-bold">
              {promotedTag}
            </span>
          )}
        </div>

        <div className="shop-tile-detailed-component-details">
          <div className="shop-tile-detailed-component-details-row">
            <span className={`shop-tile-detailed-component-details-name${disabledText ? ' disabled-opc-full' : ''}`}>
              {name}
            </span>
            <ShopRating shop={shop} />
          </div>

          <div className={`shop-tile-detailed-component-details-row${disabledText ? ' disabled-opc-half' : ''}`}>
            <ShopTileDeliveryInfo shop={shop} />
            {isNew && <span className="shop-tile-detailed-component-details-new-tag">NEW</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ShopTileDetailed, (prev, next) => {
  return prev.shop._id === next.shop._id;
});
